import { Icon3dBox, IconReceiptTax } from '@sequencehq/core-components'
import { fetchQuery } from '@sequencehq/api'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { apiQueryClient } from 'features/api/apiQueryClient'
import { MINUTES } from '@sequencehq/utils/dates'

type ProductItem =
  DashboardApi20240730.GetBillingProducts.BillingProductsList['items'][number]

const PAGE_SIZE = 100

const fetchProducts = async (searchTerm: string) => {
  const data = await fetchQuery(
    apiQueryClient,
    dashboard20240730Client.getBillingProducts,
    {
      name: searchTerm || undefined,
      limit: PAGE_SIZE
    },
    { staleTime: 1 * MINUTES }
  )
  return data?.items ?? []
}

const fetchProductsPaginated = async (searchTerm: string, cursor?: string) => {
  const data = await fetchQuery(
    apiQueryClient,
    dashboard20240730Client.getBillingProducts,
    {
      name: searchTerm || undefined,
      limit: PAGE_SIZE,
      after: cursor
    },
    { staleTime: 1 * MINUTES }
  )

  const items = data?.items ?? []
  const pagination = data?.pagination

  return {
    items,
    cursor: pagination?.after,
    hasMore: items.length === PAGE_SIZE && Boolean(pagination?.after)
  }
}

const fetchProductsByIds = async (ids: string[]) => {
  // Resolve each previously-selected id so the filter pill restores the name
  // when the page is reloaded with `productId` in the URL. Misses are dropped
  // rather than failing the whole resolution.
  const results = await Promise.all(
    ids.map(async id => {
      try {
        const response = await dashboardv99990101Client.getProduct({ id })
        return response.data
      } catch {
        return undefined
      }
    })
  )
  return results.filter(
    (product): product is NonNullable<typeof product> =>
      product !== undefined && product !== null
  )
}

export const productIdFilterConfig = {
  type: 'multiSelectAsync' as const,
  label: 'Product',
  icon: Icon3dBox,
  fetcher: fetchProducts,
  paginatedFetcher: fetchProductsPaginated,
  initializeOptions: fetchProductsByIds,
  mapToOption: (product: Pick<ProductItem, 'id' | 'name'>) => ({
    label: product.name ?? product.id,
    value: product.id
  })
}

type TaxCategoryItem = {
  id: string
  name: string
}

const TAX_CATEGORY_PAGE_SIZE = 100

// The tax categories endpoint doesn't support a server-side name search,
// so we filter each page client-side. Cursor-based pagination still lets
// users scroll through pages to reach matches beyond the first page.
const filterByName = (
  items: ReadonlyArray<TaxCategoryItem>,
  searchTerm: string
): TaxCategoryItem[] => {
  const term = searchTerm.trim().toLowerCase()
  if (!term) return [...items]
  return items.filter(item => item.name.toLowerCase().includes(term))
}

const fetchTaxCategories = async (
  searchTerm: string
): Promise<TaxCategoryItem[]> => {
  const data = await fetchQuery(
    apiQueryClient,
    dashboard20240730Client.getTaxCategories,
    { limit: TAX_CATEGORY_PAGE_SIZE },
    { staleTime: 1 * MINUTES }
  )
  return filterByName(data?.items ?? [], searchTerm)
}

const fetchTaxCategoriesPaginated = async (
  searchTerm: string,
  cursor?: string
) => {
  const data = await fetchQuery(
    apiQueryClient,
    dashboard20240730Client.getTaxCategories,
    {
      limit: TAX_CATEGORY_PAGE_SIZE,
      after: cursor
    },
    { staleTime: 1 * MINUTES }
  )

  const items = data?.items ?? []
  const pagination = data?.pagination

  return {
    items: filterByName(items, searchTerm),
    cursor: pagination?.after,
    hasMore:
      items.length === TAX_CATEGORY_PAGE_SIZE && Boolean(pagination?.after)
  }
}

const fetchTaxCategoriesByIds = async (
  ids: string[]
): Promise<TaxCategoryItem[]> => {
  const results = await Promise.all(
    ids.map(async id => {
      try {
        const response = await dashboard20240730Client.getTaxCategory({ id })
        return response.data
      } catch {
        return undefined
      }
    })
  )
  return results.filter(
    (taxCategory): taxCategory is NonNullable<typeof taxCategory> =>
      taxCategory !== undefined && taxCategory !== null
  )
}

export const taxCategoryIdFilterConfig = {
  type: 'multiSelectAsync' as const,
  label: 'Tax category',
  icon: IconReceiptTax,
  fetcher: fetchTaxCategories,
  paginatedFetcher: fetchTaxCategoriesPaginated,
  initializeOptions: fetchTaxCategoriesByIds,
  mapToOption: (taxCategory: TaxCategoryItem) => ({
    label: taxCategory.name,
    value: taxCategory.id
  })
}
